import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { borderRadii, scrollbarCss, gutters, layers } from 'app/spacing';
import { shadowLight } from 'app/colors';
import { Table as KizenTable, TRow } from 'components/Kizen/Table';
import {
  useEndsOfYScroll,
  useScrollThreshold,
  GradientBottom,
  GradientRight,
  Loader,
  TableText,
} from './helpers';
import useWidthMeasurement from 'hooks/useWidthMeasurement';
import { mergeRefs } from '@kizen/kds/util';
import { TableLoader } from 'components/Tables/Big/TableLoader';
import { useState } from 'react';
import { useDebounce } from 'react-use';

const HEADER_HEIGHT = 52;
const ROW_HEIGHT = 40;

const Table = styled(KizenTable)`
  position: relative;
  border-collapse: separate; // Ensures bottom border on sticky th doesn't scroll away
  tr:not(.collapsible) {
    height: ${ROW_HEIGHT}px;
  }

  tr.virtual td {
    height: ${ROW_HEIGHT}px;
  }

  && thead {
    border-bottom: 1px solid var(--color-border-medium-light);
    tr {
      height: ${HEADER_HEIGHT}px;
      z-index: ${layers.content(0, 2)};
      th {
        vertical-align: middle;
        ${({ inModal }) =>
          inModal &&
          css`
            border-top: 1px solid transparent; // stop the pop on hover
          `}
      }
      th:not(.Resizing) .ResizerLayout {
        display: none;
      }
      &:hover th .ResizerLayout {
        display: flex;
      }
    }
  }

  tbody {
    tr:hover {
      background-color: var(--color-background-hover);
    }

    tr.selected {
      background-color: var(--color-background-hover);
      a {
        cursor: default;
        text-decoration: none;

        :hover,
        :focus {
          color: var(--color-button-secondary-default);
        }
      }
      &&:hover {
        background-color: var(--color-background-hover);
      }
    }

    tr.collapsible:hover {
      background-color: var(--color-background-white);
    }

    // Make last menu open upwards
    tr:last-of-type .IconButtonMenu__menu {
      transform: translateY(-100%) translateY(-24px) translateX(-100%);
    }

    tr.collapsible > td:first-child > * {
      background: var(--color-background-hover);
      > * {
        background: var(--color-background-hover);
        padding-left: 40px;
        margin-left: -70px;
      }
    }
  }
`;

const TableScrollWrapper = styled.div`
  flex-grow: 1;
  flex-basis: 0;
  margin-right: ${({ inModal }) => (inModal ? gutters.spacing(0, -5) : 0)}px;
  &.big-table {
    flex-basis: auto;
    flex: 1;
    height: 100%;
    border-radius: ${borderRadii.small};
  }
  &.in-modal {
    flex-basis: auto;
    flex: 1;
    height: 100%;
    overflow-y: scroll;
    border-radius: ${borderRadii.small};
    border: solid 1px var(--color-border-medium);
    table {
      width: 100%;
    }
  }
  overflow-y: auto;
  ${scrollbarCss({ track: false })}
  &::-webkit-scrollbar-track {
    // A hack for scrollbar to appear below sticky header.
    // Much better than the alternative hacks to detach/size/scroll the header,
    // especially since we already have a custom scrollbar.
    margin-top: calc(${HEADER_HEIGHT}px - 1px);
  }
  table {
    width: 100%;
  }
  thead th {
    position: sticky;
    top: 0;
    background-color: var(--color-background-white);
    z-index: ${layers.content(
      0,
      1
    )}; // For some reason the editor toolbar was appearing on top of these
  }

  ${({ headerShadow }) =>
    headerShadow
      ? css`
          thead {
            position: sticky;
            top: 0;
            background: var(--color-background-white);
            ${shadowLight};
            z-index: ${layers.content(0, 9)};
          }
        `
      : ''}

  ${({ virtualized }) =>
    virtualized
      ? css`
          thead {
            position: sticky;
            z-index: ${layers.content(0, 9)};
          }
        `
      : ''}
  ${({ staleData }) =>
    staleData &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

export default function LazyLoadTable({
  className,
  loading,
  fetchingNextPage,
  columns,
  headData,
  onScrollThreshold,
  data,
  noDataMessage = '',
  children,
  loaderSize = 50,
  noLoaderPadding = false,
  inModal,
  tableBodyRef,
  virtualScrollerRef,
  virtualized = false,
  staleData = false,
  totalSize,
  hideBottomGradient = false,
  ...others
}) {
  const {
    ref: wrapperRef,
    plainRef: tableScrollRef,
    width,
  } = useWidthMeasurement();
  const { ref: tableRef, width: tableWidth } = useWidthMeasurement();

  // Depend on templates so we calculate scroll bottom when layout's data and thereby its height updates
  // also useEffect in useEndsOfYScroll fires before the virtual items' geometry updated
  // so we need to force a remeasure with timeout
  const [forceScrollMeasure, setForceScrollMeasure] = useState(0);
  const [scrolledTop, scrolledBottom] = useEndsOfYScroll(tableScrollRef, [
    forceScrollMeasure,
  ]);
  useDebounce(
    () => {
      setForceScrollMeasure((prev) => prev + 1);
    },
    100,
    [totalSize]
  );

  useScrollThreshold(tableScrollRef, [data], onScrollThreshold);

  return (
    <>
      <TableScrollWrapper
        className={className}
        staleData={staleData}
        ref={mergeRefs(wrapperRef, virtualScrollerRef)}
        inModal={inModal}
        headerShadow={!scrolledTop}
        virtualized={virtualized}
      >
        <Table
          head={<TRow head columns={columns} data={headData} {...others} />}
          inModal={inModal}
          ref={tableRef}
          tableBodyRef={tableBodyRef}
          forceDisplayBlock
        >
          {!loading && children}
        </Table>
        {noDataMessage && !loading && data && data.length === 0 && (
          <TableText color="var(--color-font-placeholder)">
            {noDataMessage}
          </TableText>
        )}
        {staleData && <TableLoader />}
        <Loader
          loading={fetchingNextPage || loading}
          noPadding={noLoaderPadding || (inModal && loading)}
          size={loaderSize}
        />
      </TableScrollWrapper>
      <GradientBottom
        fadeIn={!scrolledBottom && !hideBottomGradient}
        inModal={inModal}
        scrollable={tableWidth > width}
      />
      {!virtualized ? <GradientRight fadeIn inModal={inModal} /> : null}
    </>
  );
}
